/**
 * Talent Acquisition MVP — client-side Vacancy Requests tab.
 * List + "Nueva Vacante" form + full-page success state (founder plan v3).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api, formatApiError } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Loader2, CheckCircle2, Paperclip, X, Pencil } from 'lucide-react';

const EMPTY = {
  position_title: '', department: '', openings: 1, employment_type: '',
  desired_start_date: '', hiring_priority: 'no_timeline', work_location: '',
  work_schedule: '', preferred_gender: 'any', age_min: '', age_max: '',
  languages: [], education_level: '', years_experience: '',
  technical_knowledge: '', competencies_text: '', budget_defined: false,
  budget_from: '', budget_to: '', comments: '',
};

const STATUS_TONE = {
  submitted: 'bg-info/10 text-info border-info/20',
  in_process: 'bg-success/10 text-success border-success/20',
  closed: 'text-muted-foreground',
};

export function VacancyRequestsClient() {
  const { t } = useTranslation();
  const [view, setView] = useState('list'); // list | form | success
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // vacancy being edited
  const [submitted, setSubmitted] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await api.get('/client/vacancy-requests').then((r) => r.data)); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (view === 'form') {
    return (
      <VacancyForm
        initial={editing}
        onCancel={() => { setEditing(null); setView('list'); }}
        onDone={(doc, isEdit) => {
          setEditing(null); load();
          if (isEdit) { setView('list'); toast.success(t('vacancy.updated', { defaultValue: 'Solicitud actualizada.' })); }
          else { setSubmitted(doc); setView('success'); }
        }}
      />
    );
  }

  if (view === 'success' && submitted) {
    return (
      <Card className="p-10 shadow-card text-center max-w-2xl mx-auto" data-testid="vacancy-success-state">
        <CheckCircle2 className="h-12 w-12 text-success mx-auto mb-4" />
        <h2 className="font-display font-bold text-lg mb-2">
          {t('vacancy.successTitle', { defaultValue: 'Tu solicitud de vacante fue enviada exitosamente a Corporate Talent Hub.' })}
        </h2>
        <p className="text-sm text-muted-foreground mb-1">
          {t('vacancy.successBody', { defaultValue: 'Uno de nuestros Advisors revisará tu solicitud e iniciará el proceso de reclutamiento próximamente.' })}
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          {t('vacancy.successTrack', { defaultValue: 'Puedes dar seguimiento al estado de tu solicitud desde esta sección.' })}
        </p>
        <p className="text-xs text-muted-foreground mb-6">
          {submitted.position_title} · {submitted.department} · {submitted.openings} {t('vacancy.openingsShort', { defaultValue: 'plazas' })}
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" onClick={() => setView('list')} data-testid="vacancy-back-to-list">
            {t('vacancy.backToList', { defaultValue: 'Volver a Vacantes' })}
          </Button>
          <Button onClick={() => { setEditing(null); setView('form'); }} data-testid="vacancy-create-another">
            {t('vacancy.createAnother', { defaultValue: 'Crear otra vacante' })}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {t('vacancy.listHint', { defaultValue: 'Solicita nuevas posiciones y da seguimiento a su estado.' })}
        </p>
        <Button size="sm" className="gap-1.5" onClick={() => { setEditing(null); setView('form'); }} data-testid="vacancy-new-btn">
          <Plus className="h-4 w-4" /> {t('vacancy.new', { defaultValue: 'Nueva Vacante' })}
        </Button>
      </div>
      {loading ? (
        <div className="py-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading')}
        </div>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center text-sm text-muted-foreground shadow-card space-y-1" data-testid="vacancy-empty">
          <p>{t('vacancy.empty', { defaultValue: 'Aún no has creado solicitudes de vacantes internas.' })}</p>
          <p className="text-xs">{t('vacancy.emptyHint', { defaultValue: 'Las postulaciones públicas que recibas aparecen en la Bandeja de candidatos de la pestaña Procesos, aunque no exista una solicitud interna.' })}</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="vacancy-list">
          {rows.map((v) => (
            <Card key={v.id} className="p-4 shadow-card flex items-center gap-3" data-testid={`vacancy-row-${v.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm">{v.position_title}</p>
                <p className="text-xs text-muted-foreground">
                  {v.department} · {v.openings} {t('vacancy.openingsShort', { defaultValue: 'plazas' })}
                  {v.desired_start_date ? ` · ${t('vacancy.start', { defaultValue: 'Inicio' })} ${v.desired_start_date}` : ''}
                </p>
              </div>
              {v.jd_file && <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
              <Badge variant="outline" className={`text-xs ${STATUS_TONE[v.status] || ''}`} data-testid={`vacancy-status-${v.id}`}>
                {t(`vacancy.statuses.${v.status}`, { defaultValue: v.status })}
              </Badge>
              {v.public_published ? (
                <>
                  <Badge variant="outline" className="text-xs bg-sky-50 text-sky-700 border-sky-200" data-testid={`vacancy-public-${v.id}`}>
                    {t('vacancy.public', { defaultValue: 'Pública' })}
                  </Badge>
                  <Button size="sm" variant="ghost" className="text-xs" data-testid={`vacancy-copylink-${v.id}`}
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/jobs/${v.public_slug}`); toast.success(t('vacancy.linkCopied', { defaultValue: 'Enlace público copiado' })); }}>
                    {t('vacancy.copyLink', { defaultValue: 'Copiar enlace' })}
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs" data-testid={`vacancy-unpublish-${v.id}`}
                    onClick={async () => { try { await api.post(`/client/vacancy-requests/${v.id}/unpublish`); load(); toast.success(t('vacancy.unpublished', { defaultValue: 'Vacante despublicada' })); } catch (e) { toast.error(formatApiError(e)); } }}>
                    {t('vacancy.unpublish', { defaultValue: 'Despublicar' })}
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline" className="text-xs" data-testid={`vacancy-publish-${v.id}`}
                  onClick={async () => { try { const r = await api.post(`/client/vacancy-requests/${v.id}/publish`); load(); navigator.clipboard.writeText(`${window.location.origin}/jobs/${r.data.public_slug}`); toast.success(t('vacancy.published', { defaultValue: 'Vacante publicada — enlace copiado' })); } catch (e) { toast.error(formatApiError(e)); } }}>
                  {t('vacancy.publish', { defaultValue: 'Publicar vacante pública' })}
                </Button>
              )}
              {v.status === 'submitted' && (
                <Button size="sm" variant="outline" className="gap-1" data-testid={`vacancy-edit-${v.id}`}
                  onClick={() => { setEditing(v); setView('form'); }}>
                  <Pencil className="h-3 w-3" /> {t('common.edit', { defaultValue: 'Editar' })}
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

const Sec = ({ title, children }) => (
  <div className="space-y-3">
    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
    {children}
  </div>
);

function VacancyForm({ initial, onCancel, onDone }) {
  const { t } = useTranslation();
  const isEdit = !!initial;
  const [f, setF] = useState(() => initial ? {
    ...EMPTY, ...initial,
    age_min: initial.age_range?.min ?? '', age_max: initial.age_range?.max ?? '',
    budget_from: initial.budget_from ?? '', budget_to: initial.budget_to ?? '',
    years_experience: initial.years_experience ?? '',
    work_location: initial.work_location || '', work_schedule: initial.work_schedule || '',
    education_level: initial.education_level || '',
    technical_knowledge: initial.technical_knowledge || '',
    competencies_text: initial.competencies_text || '',
    comments: initial.comments || '',
  } : { ...EMPTY });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const addLang = () => set('languages', [...f.languages, { language: '', proficiency: 'intermediate' }]);
  const setLang = (i, k, v) => set('languages', f.languages.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const rmLang = (i) => set('languages', f.languages.filter((_, j) => j !== i));

  const submit = async () => {
    const required = ['position_title', 'department', 'employment_type', 'desired_start_date'];
    if (required.some((k) => !String(f[k] || '').trim()) || !f.openings) {
      toast.error(t('vacancy.requiredMissing', { defaultValue: 'Completa los campos obligatorios (*)' }));
      return;
    }
    if (f.budget_defined && (f.budget_from === '' || f.budget_to === '')) {
      toast.error(t('vacancy.budgetMissing', { defaultValue: 'Indica el rango de presupuesto.' }));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        position_title: f.position_title.trim(), department: f.department.trim(),
        openings: Number(f.openings), employment_type: f.employment_type,
        desired_start_date: f.desired_start_date,
        hiring_priority: f.hiring_priority,
        work_location: f.work_location?.trim() || null,
        work_schedule: f.work_schedule || null,
        preferred_gender: f.preferred_gender,
        age_range: f.age_min !== '' && f.age_max !== '' ? { min: Number(f.age_min), max: Number(f.age_max) } : null,
        languages: f.languages.filter((l) => l.language.trim()),
        education_level: f.education_level || null,
        years_experience: f.years_experience === '' ? null : Number(f.years_experience),
        technical_knowledge: f.technical_knowledge?.trim() || null,
        competencies_text: f.competencies_text?.trim() || null,
        budget_defined: f.budget_defined,
        budget_from: f.budget_defined ? Number(f.budget_from) : null,
        budget_to: f.budget_defined ? Number(f.budget_to) : null,
        comments: f.comments?.trim() || null,
      };
      const doc = isEdit
        ? await api.put(`/client/vacancy-requests/${initial.id}`, payload).then((r) => r.data)
        : await api.post('/client/vacancy-requests', payload).then((r) => r.data);
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/client/vacancy-requests/${doc.id}/jd-file`, fd);
      }
      onDone(doc, isEdit);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-6 shadow-card space-y-6 max-w-3xl" data-testid="vacancy-form">
      <h2 className="font-display font-bold">
        {isEdit ? t('vacancy.editTitle', { defaultValue: 'Editar Vacante' }) : t('vacancy.new', { defaultValue: 'Nueva Vacante' })}
      </h2>

      <Sec title={t('vacancy.secPosition', { defaultValue: 'Posición' })}>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>{t('vacancy.positionTitle', { defaultValue: 'Título de la posición' })} *</Label>
            <Input data-testid="vacancy-position" value={f.position_title} onChange={(e) => set('position_title', e.target.value)} /></div>
          <div><Label>{t('vacancy.department', { defaultValue: 'Departamento' })} *</Label>
            <Input data-testid="vacancy-department" value={f.department} onChange={(e) => set('department', e.target.value)} /></div>
          <div><Label>{t('vacancy.openings', { defaultValue: 'Nº de plazas' })} *</Label>
            <Input data-testid="vacancy-openings" type="number" min={1} value={f.openings} onChange={(e) => set('openings', e.target.value)} /></div>
          <div><Label>{t('vacancy.employmentType', { defaultValue: 'Tipo de empleo' })} *</Label>
            <Select value={f.employment_type} onValueChange={(v) => set('employment_type', v)}>
              <SelectTrigger data-testid="vacancy-employment-type"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {['permanent', 'temporary', 'contractor', 'internship'].map((v) => (
                  <SelectItem key={v} value={v}>{t(`vacancy.employmentTypes.${v}`, { defaultValue: v })}</SelectItem>))}
              </SelectContent>
            </Select></div>
          <div><Label>{t('vacancy.startDate', { defaultValue: 'Fecha deseada de inicio' })} *</Label>
            <Input data-testid="vacancy-start-date" type="date" value={f.desired_start_date} onChange={(e) => set('desired_start_date', e.target.value)} /></div>
          <div><Label>{t('vacancy.priority', { defaultValue: 'Prioridad de contratación' })}</Label>
            <Select value={f.hiring_priority} onValueChange={(v) => set('hiring_priority', v)}>
              <SelectTrigger data-testid="vacancy-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['urgent', 'within_30_days', 'within_60_days', 'no_timeline'].map((v) => (
                  <SelectItem key={v} value={v}>{t(`vacancy.priorities.${v}`, { defaultValue: v })}</SelectItem>))}
              </SelectContent>
            </Select></div>
          <div><Label>{t('vacancy.location', { defaultValue: 'Ubicación' })}</Label>
            <Input data-testid="vacancy-location" value={f.work_location} onChange={(e) => set('work_location', e.target.value)}
              placeholder={t('vacancy.locationPh', { defaultValue: 'Ciudad de Panamá, Remoto, Híbrido…' })} /></div>
          <div><Label>{t('vacancy.schedule', { defaultValue: 'Horario' })}</Label>
            <Select value={f.work_schedule || 'none'} onValueChange={(v) => set('work_schedule', v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="vacancy-schedule"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {['full_time', 'part_time', 'shifts', 'other'].map((v) => (
                  <SelectItem key={v} value={v}>{t(`vacancy.schedules.${v}`, { defaultValue: v })}</SelectItem>))}
              </SelectContent>
            </Select></div>
        </div>
      </Sec>

      <Sec title={t('vacancy.secProfile', { defaultValue: 'Perfil deseado (opcional)' })}>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Label>{t('vacancy.gender', { defaultValue: 'Género preferido' })}</Label>
            <Select value={f.preferred_gender} onValueChange={(v) => set('preferred_gender', v)}>
              <SelectTrigger data-testid="vacancy-gender"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['any', 'female', 'male'].map((v) => (
                  <SelectItem key={v} value={v}>{t(`vacancy.genders.${v}`, { defaultValue: v })}</SelectItem>))}
              </SelectContent>
            </Select></div>
          <div><Label>{t('vacancy.ageMin', { defaultValue: 'Edad mín.' })}</Label>
            <Input data-testid="vacancy-age-min" type="number" value={f.age_min} onChange={(e) => set('age_min', e.target.value)} /></div>
          <div><Label>{t('vacancy.ageMax', { defaultValue: 'Edad máx.' })}</Label>
            <Input data-testid="vacancy-age-max" type="number" value={f.age_max} onChange={(e) => set('age_max', e.target.value)} /></div>
          <div><Label>{t('vacancy.education', { defaultValue: 'Educación' })}</Label>
            <Select value={f.education_level || 'none'} onValueChange={(v) => set('education_level', v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="vacancy-education"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {['high_school', 'technical', 'bachelor', 'master', 'phd', 'other'].map((v) => (
                  <SelectItem key={v} value={v}>{t(`vacancy.educationLevels.${v}`, { defaultValue: v })}</SelectItem>))}
              </SelectContent>
            </Select></div>
          <div><Label>{t('vacancy.experience', { defaultValue: 'Años de experiencia' })}</Label>
            <Input data-testid="vacancy-experience" type="number" min={0} value={f.years_experience} onChange={(e) => set('years_experience', e.target.value)} /></div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label>{t('vacancy.languages', { defaultValue: 'Idiomas requeridos' })}</Label>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={addLang} data-testid="vacancy-add-language">
              <Plus className="h-3 w-3 mr-1" />{t('vacancy.addLanguage', { defaultValue: 'Agregar idioma' })}
            </Button>
          </div>
          {f.languages.map((l, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <Input className="flex-1" placeholder={t('vacancy.languagePh', { defaultValue: 'Idioma (p. ej. Inglés)' })}
                value={l.language} onChange={(e) => setLang(i, 'language', e.target.value)} data-testid={`vacancy-language-${i}`} />
              <Select value={l.proficiency} onValueChange={(v) => setLang(i, 'proficiency', v)}>
                <SelectTrigger className="w-40" data-testid={`vacancy-proficiency-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['basic', 'intermediate', 'advanced', 'native'].map((v) => (
                    <SelectItem key={v} value={v}>{t(`vacancy.proficiencies.${v}`, { defaultValue: v })}</SelectItem>))}
                </SelectContent>
              </Select>
              <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={() => rmLang(i)}><X className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
        <div><Label>{t('vacancy.technical', { defaultValue: 'Conocimientos técnicos requeridos' })}</Label>
          <Textarea data-testid="vacancy-technical" rows={2} value={f.technical_knowledge} onChange={(e) => set('technical_knowledge', e.target.value)} /></div>
        <div><Label>{t('vacancy.competencies', { defaultValue: 'Describe las competencias o características que consideras importantes para esta posición.' })}</Label>
          <Textarea data-testid="vacancy-competencies" rows={3} value={f.competencies_text} onChange={(e) => set('competencies_text', e.target.value)} /></div>
      </Sec>

      <Sec title={t('vacancy.secCompensation', { defaultValue: 'Compensación' })}>
        <div className="flex items-center gap-3">
          <Switch checked={f.budget_defined} onCheckedChange={(v) => set('budget_defined', v)} data-testid="vacancy-budget-switch" />
          <span className="text-sm">{t('vacancy.budgetDefined', { defaultValue: '¿Presupuesto definido?' })}</span>
        </div>
        {f.budget_defined && (
          <div className="grid grid-cols-2 gap-3 max-w-sm">
            <div><Label>{t('vacancy.budgetFrom', { defaultValue: 'Desde' })}</Label>
              <Input data-testid="vacancy-budget-from" type="number" min={0} value={f.budget_from} onChange={(e) => set('budget_from', e.target.value)} /></div>
            <div><Label>{t('vacancy.budgetTo', { defaultValue: 'Hasta' })}</Label>
              <Input data-testid="vacancy-budget-to" type="number" min={0} value={f.budget_to} onChange={(e) => set('budget_to', e.target.value)} /></div>
          </div>
        )}
      </Sec>

      <Sec title={t('vacancy.secAttachments', { defaultValue: 'Adjuntos y comentarios' })}>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden" data-testid="vacancy-jd-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => fileRef.current?.click()} data-testid="vacancy-jd-btn">
            <Paperclip className="h-3.5 w-3.5" />
            {t('vacancy.attachJd', { defaultValue: 'Adjuntar Job Description (PDF/DOCX)' })}
          </Button>
          {file && <span className="text-xs text-muted-foreground">{file.name}</span>}
          {!file && initial?.jd_file && <span className="text-xs text-muted-foreground">{initial.jd_file.filename}</span>}
        </div>
        <div><Label>{t('vacancy.comments', { defaultValue: 'Comentarios adicionales' })}</Label>
          <Textarea data-testid="vacancy-comments" rows={2} value={f.comments} onChange={(e) => set('comments', e.target.value)} /></div>
      </Sec>

      <div className="flex items-center justify-end gap-3 pt-2 border-t">
        <Button variant="outline" onClick={onCancel} data-testid="vacancy-cancel">{t('common.cancel', { defaultValue: 'Cancelar' })}</Button>
        <Button onClick={submit} disabled={saving} data-testid="vacancy-submit">
          {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
          {isEdit ? t('common.save', { defaultValue: 'Guardar' }) : t('vacancy.submit', { defaultValue: 'Enviar solicitud' })}
        </Button>
      </div>
    </Card>
  );
}

export default VacancyRequestsClient;
